
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScoreBreakdown, Question } from '@/types/survey';
import { analyzeSentimentDetailed } from '@/services/aiAnalyticsService';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, Lightbulb, MessageCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface SentimentAnalysisProps {
  scoreBreakdown: ScoreBreakdown | null;
  responses: Record<string, any>;
  questions: Question[];
}

const SentimentAnalysis: React.FC<SentimentAnalysisProps> = ({ 
  scoreBreakdown, 
  responses, 
  questions 
}) => {
  // Only perform analysis if we have valid responses and questions
  const sentiment = React.useMemo(() => {
    if (!responses || Object.keys(responses).length === 0 || !questions || questions.length === 0) {
      return null;
    }
    return analyzeSentimentDetailed(responses, questions);
  }, [responses, questions]);

  if (!scoreBreakdown) {
    return <div className="text-center py-6">No data available for analysis</div>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Enhanced Sentiment Analysis</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {sentiment && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <h3 className="font-medium text-gray-700 mb-2">Overall Sentiment Score</h3>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold text-blue-600">{sentiment.score.toFixed(1)}</div>
                    <div className="text-sm text-gray-500">(Out of 5)</div>
                  </div>
                  <div className="mt-2 w-full bg-gray-100 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{ width: `${(sentiment.score / 5) * 100}%` }}
                    ></div>
                  </div>
                  <div className="mt-3 text-sm text-gray-600">
                    <span className="font-medium">Confidence:</span> {(sentiment.confidence * 100).toFixed(0)}%
                  </div>
                  
                  {/* Sentiment score interpretation */}
                  <div className="mt-2 text-sm">
                    {sentiment.score >= 4.5 ? (
                      <div className="text-green-600">Exceptionally positive sentiment</div>
                    ) : sentiment.score >= 3.5 ? (
                      <div className="text-emerald-600">Positive sentiment</div>
                    ) : sentiment.score >= 2.5 ? (
                      <div className="text-amber-600">Neutral sentiment</div>
                    ) : sentiment.score >= 1.5 ? (
                      <div className="text-orange-600">Somewhat negative sentiment</div>
                    ) : (
                      <div className="text-red-600">Significantly negative sentiment</div>
                    )}
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                  <h3 className="font-medium text-gray-700 mb-3">Dominant Emotions</h3>
                  <div className="space-y-3">
                    {sentiment.emotions.map((emotion, index) => {
                      const intensity = sentiment.emotionIntensity?.[emotion] || 0.5;
                      let emotionColor = "bg-blue-100 text-blue-700";
                      
                      // Color coding based on emotion
                      switch(emotion) {
                        case 'joy':
                        case 'satisfaction':
                        case 'enthusiasm':
                          emotionColor = "bg-green-100 text-green-700 border-green-200";
                          break;
                        case 'trust':
                        case 'interest':
                          emotionColor = "bg-blue-100 text-blue-700 border-blue-200";
                          break;
                        case 'anticipation':
                          emotionColor = "bg-amber-100 text-amber-700 border-amber-200";
                          break;
                        case 'fear':
                        case 'confusion':
                          emotionColor = "bg-orange-100 text-orange-700 border-orange-200";
                          break;
                        case 'anger':
                        case 'disgust':
                        case 'sadness':
                          emotionColor = "bg-red-100 text-red-700 border-red-200";
                          break;
                        case 'surprise':
                          emotionColor = "bg-purple-100 text-purple-700 border-purple-200";
                          break;
                        default:
                          emotionColor = "bg-gray-100 text-gray-700 border-gray-200";
                      }
                      
                      return (
                        <div key={index} className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className={`px-3 py-1 rounded-full text-sm ${emotionColor} border`}>
                              {emotion.charAt(0).toUpperCase() + emotion.slice(1)}
                            </span>
                            <span className="text-xs text-gray-500">
                              {intensity >= 0.8 ? "High intensity" : 
                               intensity >= 0.5 ? "Medium intensity" : "Low intensity"}
                            </span>
                          </div>
                          <Progress 
                            value={intensity * 100} 
                            className={`h-1 ${
                              intensity >= 0.8 ? "bg-gray-100" : 
                              intensity >= 0.5 ? "bg-gray-100" : "bg-gray-100"
                            }`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              {/* Topic Distribution */}
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <h3 className="font-medium text-gray-700 mb-3">Topic Distribution</h3>
                <div className="grid grid-cols-2 gap-4">
                  {sentiment.topicDistribution && Object.entries(sentiment.topicDistribution)
                    .sort(([, scoreA], [, scoreB]) => (scoreB as number) - (scoreA as number))
                    .slice(0, 6)
                    .map(([topic, score], index) => (
                      <div key={index} className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-700">{topic}</span>
                          <span className="text-xs text-gray-500">{((score as number) * 100).toFixed(0)}%</span>
                        </div>
                        <Progress value={(score as number) * 100} className="h-2" />
                      </div>
                    ))}
                </div>
              </div>
              
              <Alert className="bg-blue-50 border-blue-100">
                <div className="flex">
                  <Info className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                  <AlertDescription className="text-sm text-blue-700">
                    The enhanced sentiment analysis identifies emotional patterns, intensities, and key topics
                    in your project's feedback. This provides deeper insight into the emotional perception of your project.
                  </AlertDescription>
                </div>
              </Alert>
              
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <h3 className="font-medium text-gray-700 mb-3">Key Themes & Topics</h3>
                <div className="flex flex-wrap gap-2">
                  {sentiment.keywords.map((keyword, index) => (
                    <span 
                      key={index}
                      className={`px-3 py-1.5 rounded-full text-sm ${
                        index < 3 
                          ? 'bg-purple-100 text-purple-700 border border-purple-200' 
                          : index < 6
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-700 border border-gray-200'
                      }`}
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
                
                {/* Keyword Insights */}
                <div className="mt-4 flex items-start gap-3">
                  <Lightbulb className="h-5 w-5 text-amber-500 mt-1 flex-shrink-0" />
                  <div className="text-sm text-gray-700">
                    <p>These keywords represent the most significant concepts and topics mentioned in the feedback. 
                    The more prominent keywords highlight areas that may require attention or represent strengths to build upon.</p>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {!sentiment && (
            <div className="text-center py-6 text-gray-500 flex flex-col items-center gap-3">
              <MessageCircle className="h-12 w-12 text-gray-300" />
              <p>Not enough textual data to perform sentiment analysis</p>
              <p className="text-sm text-gray-400">Add more detailed responses to enable this feature</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SentimentAnalysis;
